import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Grid, Box, Typography, Alert, Skeleton, Chip } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import BookingWidget from "./common/BookingWidget";
import ImageSlider from "./common/ImageSlider";
import YachtInfo from "./common/YachtInfo";
import { getYachtDetails } from "../utils/boatApi";
import { Link as RouterLink } from "react-router-dom"; // Import Link from React Router
import boatPlaceholder from "../assets/images/boat.jpg";
import HeaderBar from "./HeaderBar";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store/store";
import SimilatBoatsWidget from "./common/SimilatBoatsWidget";
import { fetchYachtDetails, clearYachtDetails } from "../store/yachtSlice";
import { addToComparator, removeFromComparator } from "../store/comparatorSlice";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ShareButton from "./common/ShareButton";
import { trackEvent } from "../utils/analytics";

const YachtDetail: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isYachtAvailabilityLoading, setIsYachtAvailabilityLoading] =
    useState(false);

  const [images, setImages] = useState<string[]>([]);
  const [yachtDetails, setYachtDetails] = useState<any>({
    year: null,
    cabins: null,
    beds: null,
    guests: null,
    bathrooms: null,
    amenities: null,
    engine: null,
    water_capacity: null,
    sail_type: null,
    fuel_capacity: null,
    length: null,
    services: [],
    // yachtAvailability: null,
    additionalServices: [],
    location: null,
    id: null,
    // price: null,
    discountPercentage: null,
    handleOpenModal: () => {},
    setFormData: () => {},
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // const [yacht, setYacht] = useState<any>(location.state?.yacht);
  const [yachtAvailability, setYachtAvailability] = useState<any>([]);
  const { similarBoats, yacht, loading, error } = useSelector(
    (state: any) => state.yacht,
  );
  const comparatorItems = useSelector((state: RootState) => state.comparator.items);

  // const [similarBoats, setSimilarBoats] = useState<any>([]);
  const [price, setPrice] = useState<number | null>(null);
  const [startPrice, setStartPrice] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    checkIn: "",
    checkOut: "",
    name: "",
    surname: "",
    email: "",
    telephone: "",
    notes: "",
  });

  const skippers =
    yachtDetails?.additionalServices?.filter((service: any) =>
      service?.name?.includes("Skipper"),
    ) || [];

  const skipper =
    skippers.find((service: any) => service?.obligatory) || skippers[0] || null;
  const skipperData = skipper
    ? {
        price: skipper?.price || 0,
        obligatory: skipper?.obligatory || false,
        name: skipper?.name || "",
        unit: skipper?.unit || "per week",
      }
    : null;

  const ogMeta = useMemo(() => {
    if (!yacht?.yachtId) return undefined;
    const model = yacht?.yacht?.model || '';
    const city = yacht?.location?.name || '';
    const df = formData.checkIn ? dayjs(formData.checkIn).format('MMM D') : '';
    const dt = formData.checkOut ? dayjs(formData.checkOut).format('MMM D') : '';
    const dateStr = df && dt ? `${df}–${dt}` : '';
    const priceVal = price || yacht?.actualPrice;
    const priceStr = priceVal
      ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(priceVal) + ' €'
      : '';
    let description = 'See this yacht available';
    if (dateStr) description += ` ${dateStr}`;
    if (priceStr) description += ` from ${priceStr}`;
    description += '. Book Now!';
    return {
      og_title: `SailArmada · ${model}${city ? ` · ${city}` : ''}`,
      og_description: description,
      og_image: images[0],
    };
  }, [yacht, formData.checkIn, formData.checkOut, price, images]);

  const handleYachtClick = (yacht: any) => {
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set("yachtId", yacht?.yachtId);
    window.open(`/yacht-details?${currentParams.toString()}`, '_blank', 'noopener,noreferrer');
  };


  const fetchYachtAvailability = async (id: any) => {
    if (!id) return;
    setIsYachtAvailabilityLoading(true);
    const currentYear = new Date().getFullYear();
    try {
      const [res, nextYearRes] = await Promise.all([
        getYachtDetails({ id, year: currentYear }),
        getYachtDetails({ id, year: currentYear + 1 }),
      ]);

      const current = Array.isArray(res?.data?.availability_summary)
        ? res.data.availability_summary
        : [];
      const next = Array.isArray(nextYearRes?.data?.availability_summary)
        ? nextYearRes.data.availability_summary
        : [];
      setYachtAvailability([...current, ...next]);
    } catch (error: any) {
      setYachtAvailability([]);
      console.error("Error fetching yacht details:", error);
    } finally {
      setIsYachtAvailabilityLoading(false);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const yachtId = searchParams.get("yachtId");

    const params = {
      date_from: searchParams.get("date_from") || "",
      date_to: searchParams.get("date_to") || "",
      country: searchParams.get("country") || "",
      yacht_type: searchParams.get("yacht_type") || "",
      bed: parseInt(searchParams.get("bed") || "0"),
      city: searchParams.get("city") || "",
      base_from_id: searchParams.get("base_from_id") || "",
      type: searchParams.get("type") || "",
      island_name: searchParams.get("island_name") || "",
      sailing_area_id: searchParams.get("sailing_area_id") || "",
    };

    setFormData((prev) => ({
      ...prev,
      checkIn: params?.date_from || "",
      checkOut: params?.date_to || "",
    }));

    if (!yachtId) {
      navigate("/", { replace: true });
    } else {
      setYachtAvailability([]);
      dispatch(clearYachtDetails());
      fetchYachtAvailability(yachtId);
      dispatch(fetchYachtDetails({ yachtId, ...params }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (!yacht?.yachtId) return;
    const sortedImages = [...(yacht?.yacht?.images || [])]
      .sort((a: { description: string }, b: { description: string }) => {
        if (a.description === "Main image") return -1;
        if (b.description === "Main image") return 1;
        return 0;
      })
      .map((img: { url: string }) => img.url);

    setImages(sortedImages || []);

    let initialServices = [...(yacht?.obligatoryExtras || [])];
    const customService = {
      name: "Deposit:  to be paid at check-in with credit card or cash",
      price: yacht?.securityDeposit,
      refundable: true,
      unit: "per week",
    };

    const alreadyExists = initialServices.some(
      (item: any) => item?.name === customService.name,
    );

    if (!alreadyExists) {
      initialServices.splice(1, 0, customService);
    }

    // const yachtAvailability = {};
    const yachtPrice = yacht?.actualPrice || null;
    setPrice(yachtPrice);
    setStartPrice(yacht?.actualStartPrice || null);
    const details = {
      year: yacht?.yacht?.year,
      cabins: yacht?.yacht?.cabins,
      beds: yacht?.yacht?.berths,
      guests: yacht?.yacht?.max_people_on_board,
      bathrooms: yacht?.yacht?.wc,
      amenities: yacht?.yacht?.equipment_json,
      engine: yacht?.yacht?.engine,
      water_capacity: yacht?.yacht?.water_capacity,
      sail_type: yacht?.yacht?.main_sail_type,
      fuel_capacity: yacht?.yacht?.fuel_capacity,
      length: yacht?.yacht?.length,
      services: initialServices,
      // yachtAvailability,
      additionalServices: yacht?.yacht?.products?.[0]?.extras,
      location: yacht?.location,
      id: yacht?.yacht?.id,
      product: yacht?.product,
      // price,
      discountPercentage: yacht?.discountPercentage,
      // handleOpenModal,
      setFormData,
    };

    setYachtDetails(details);
  }, [yacht]);

  useEffect(() => {
    if (!yachtAvailability.length) return;
    const week = yachtAvailability.find((w: any) =>
      w.start_date?.slice(0, 10) === formData.checkIn?.slice(0, 10) &&
      w.end_date?.slice(0, 10) === formData.checkOut?.slice(0, 10)
    );
    if (week) {
      setPrice(week.price || null);
      setStartPrice(week.startPrice || null);
    } else if (formData.checkIn && formData.checkOut) {
      setPrice(null);
      setStartPrice(null);
    }
  }, [formData.checkIn, formData.checkOut, yachtAvailability]);

  if (loading || (!yacht?.yachtId && !error)) {
    return (
      <Box>
        <HeaderBar />
        <Box sx={{ pt: "calc(var(--banner-height, 0px) + var(--nav-height, 68px))", mb: 6, maxWidth: "1200px", mx: "auto", px: { xs: 0, md: "2rem" } }}>
          <Grid container spacing={4} style={{ marginTop: "0px" }}>
            <Grid item xs={12} md={8} sx={{ pt: { xs: "15px !important", md: "32px" } }}>
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: "10px", mb: 2 }} />
              <Skeleton variant="text" width="70%" height={40} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="50%" height={30} sx={{ mb: 3 }} />
              <Typography variant="h5" py={3}>Availability</Typography>
              <Box px={3}>
                <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={1} py={2}>
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: "4px" }} />
                  ))}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <Skeleton variant="rectangular" height={420} sx={{ borderRadius: "8px" }} />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }

  if (!yacht?.yachtId) {
    return <Typography variant="h6">Unable to load yacht details. Please go back and try again.</Typography>;
  }

  const UNAVAILABLE_STATUSES = new Set([
    "Reserved", "Reserved for Owner", "Regatta: Reserved",
    "Sleepaboard: Not Available", "Service",
  ]);

  const OPTION_STATUSES = new Set([
    "Optioned",
    "Optioned on Waiting",
  ]);

  const checkInDate = formData.checkIn?.slice(0, 10);
  const checkOutDate = formData.checkOut?.slice(0, 10);

  const searchedWeek = Array.isArray(yachtAvailability) && checkInDate && checkOutDate
    ? yachtAvailability.find(
        (w: any) =>
          w.start_date?.slice(0, 10) === checkInDate &&
          w.end_date?.slice(0, 10) === checkOutDate
      )
    : null;

  const isOnOption = searchedWeek != null
    ? OPTION_STATUSES.has(searchedWeek.status)
    : Number(yacht?.status) === 2 || Number(yacht?.status) === 3;

  const isUnavailable =
    searchedWeek != null &&
    UNAVAILABLE_STATUSES.has(searchedWeek.status) &&
    !isOnOption;

  // const images = yacht?.yacht?.images
  //   ?.sort((a: { description: string }, b: { description: string }) => {
  //     if (a.description === "Main image") return -1;
  //     if (b.description === "Main image") return 1;
  //     return 0;
  //   })
  //   .map((img: { url: string }) => img.url);

  return (
    <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}>
      {/* <ReservationModal
        open={isModalOpen}
        onClose={handleCloseModal}
        images={
          yacht?.yacht?.images?.length > 0 ? yacht?.yacht?.images : coverPhoto
        }
        name={`${yacht?.yacht?.model}
        ${yacht?.yacht?.cabins_note && `- ${yacht?.yacht?.cabins_note} Cab`} -
        ${yacht?.yacht?.home_base_name} `}
        kind={yacht?.yacht?.kind}
        price={price || 0}
        formData={formData}
        setFormData={setFormData}
        yachtDetails={yacht}
        id={yacht?.yachtId}
        startPrice={startPrice || 0}
      /> */}

      <HeaderBar />
      <Box
        sx={{
          mt: 0,
          pt: "calc(var(--banner-height, 0px) + var(--nav-height, 68px))",
          mb: 6,
          maxWidth: "1200px",
          mx: "auto",
          px: {
            xs: 0,
            md: "2rem",
          },
        }}
      >
        <Box sx={{ height: "1.5rem" }} />

        <Typography
          variant="h5"
          sx={{
            padding: "0", // Default padding
            "@media (max-width: 600px)": {
              display: "none", // Hide on phone screens
            },
            "@media (max-width: 1200px)": {
              px: "1rem", // Padding for small screens
            },
          }}
        >
          Charter {yacht?.yacht?.model}{" "}
          {yacht?.yacht?.cabins_note && `- ${yacht?.yacht?.cabins_note} Cab`} -{" "}
          {yacht?.yacht?.home_base_name}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            padding: "0", // Default padding
            "@media (min-width: 600px)": {
              display: "none", // Hide on phone screens
            },
            "@media (max-width: 1200px)": {
              px: "1rem", // Padding for small screens
            },
          }}
        >
          {yacht?.yacht?.model} - {yacht?.yacht?.home_base_name}
        </Typography>
        {yacht?.startBase && yacht?.endBase && yacht?.startBase !== yacht?.endBase && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, mx: { xs: "1rem", md: 0 }, flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary">
              {yacht.startBase} → {yacht.endBase}
            </Typography>
            <Chip
              label="One Way"
              size="small"
              sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 600, fontSize: "11px", height: "20px", border: "1px solid #90caf9" }}
            />
          </Box>
        )}
        {isUnavailable && (
          <Alert
            severity="warning"
            sx={{
              mt: 2,
              mx: { xs: "1rem", md: 0 },
              borderRadius: "8px",
              fontSize: "0.95rem",
            }}
          >
            <strong>Not available for your selected dates.</strong> This yacht
            is already reserved for{" "}
            {formData.checkIn
              ? new Date(formData.checkIn).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                })
              : ""}
            {" – "}
            {formData.checkOut
              ? new Date(formData.checkOut).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })
              : ""}
            . Please choose a different week from the availability calendar
            below.
          </Alert>
        )}

        {isOnOption && (
          <Alert
            severity="info"
            sx={{
              mt: 2,
              mx: { xs: "1rem", md: 0 },
              borderRadius: "8px",
              fontSize: "0.95rem",
            }}
          >
            <strong>
              This yacht is currently under option with another client
              {yacht?.expirationDate ? ` until ${dayjs(yacht.expirationDate).format("DD/MM/YYYY")}` : ''}.
            </strong>{" "}
            You can still submit a reservation request — we will place you on
            the waiting list and confirm as soon as the option expires or is released.
          </Alert>
        )}

        <Grid container spacing={4} style={{ marginTop: "0px" }}>
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              pt: {
                xs: "15px !important",
                md: "32px",
              },
            }}
          >
            {images?.length > 0 ? (
              <ImageSlider images={images} />
            ) : (
              <Box
                sx={{
                  height: "300px",
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
              >
                <img
                  src={boatPlaceholder}
                  alt="Yacht"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>
            )}

            <YachtInfo
              {...yachtDetails}
              yachtAvailability={yachtAvailability}
              price={price}
              setPrice={setPrice}
              isYachtAvailabilityLoading={isYachtAvailabilityLoading}
              formData={formData}
              startPrice={startPrice}
              setStartPrice={setStartPrice}
              skipperData={skipperData}
              status={yacht?.status}
              isUnavailable={!!isUnavailable}
              isOnOption={!!isOnOption}
              actionBar={yacht?.yachtId && (() => {
                const yid = String(yacht.yachtId);
                const inComparator = comparatorItems.some((i) => i.yachtId === yid);
                const full = !inComparator && comparatorItems.length >= 4;
                return (
                  <Box sx={{ display: { xs: "flex", md: "none" }, gap: 2, py: 3, px: "2rem", flexWrap: "wrap", alignItems: "center", borderTop: "1px solid #e0e0e0" }}>
                    <ShareButton url={window.location.href} title={yacht?.yacht?.model} label="Share" size="small" og={ogMeta} onShare={() => trackEvent('boat_shared', { yacht_model: yacht?.yacht?.model })} />
                    <Tooltip
                      title={full ? "Comparator full — remove a boat first" : inComparator ? "Remove from comparison" : "Compare up to 4 boats side by side and find the best value"}
                      arrow
                      placement="top-start"
                    >
                      <span>
                        <Button
                          variant={inComparator ? "contained" : "outlined"}
                          disabled={full}
                          startIcon={<CompareArrowsIcon />}
                          size="small"
                          onClick={() => {
                            if (inComparator) {
                              dispatch(removeFromComparator(yid));
                            } else {
                              const mainImg = (yacht?.yacht?.images || []).find((im: any) => im.description === "Main image");
                              const urlP = new URLSearchParams(location.search);
                              dispatch(addToComparator({
                                yachtId: yid,
                                model: yacht?.yacht?.model || 'Unknown',
                                imageUrl: mainImg?.url || '',
                                price: yacht.actualPrice || 0,
                                dateFrom: urlP.get('date_from') || yacht.dateFrom || '',
                                dateTo: urlP.get('date_to') || yacht.dateTo || '',
                              }));
                              trackEvent('compare_added', { yacht_model: yacht?.yacht?.model });
                            }
                          }}
                          sx={{
                            borderRadius: "20px",
                            alignItems: "center",
                            "& .MuiButton-startIcon": { display: "flex", alignItems: "center", marginTop: 0, marginBottom: 0 },
                          "& .MuiButton-startIcon svg": { display: "block" },
                            ...(inComparator ? {
                              bgcolor: "#1565c0",
                              "&:hover": { bgcolor: "#0d47a1" },
                            } : {
                              borderColor: "#1565c0",
                              color: "#1565c0",
                              fontWeight: 600,
                              "&:hover": { bgcolor: "#e3f0ff", borderColor: "#1565c0" },
                            }),
                          }}
                        >
                          {inComparator ? "✓ Added" : full ? "Full (max 4)" : "Compare"}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                );
              })()}
            />
          </Grid>
          {/* Booking Widget */}
          <Grid item xs={12} md={4}>
            {/* Action bar — desktop only; mobile version is in the left column above */}
            {yacht?.yachtId && (() => {
              const yid = String(yacht.yachtId);
              const inComparator = comparatorItems.some((i) => i.yachtId === yid);
              const full = !inComparator && comparatorItems.length >= 4;
              return (
                <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                  <ShareButton url={window.location.href} title={yacht?.yacht?.model} label="Share" size="small" og={ogMeta} onShare={() => trackEvent('boat_shared', { yacht_model: yacht?.yacht?.model })} />
                  <Tooltip
                    title={full ? "Comparator full — remove a boat first" : inComparator ? "Remove from comparison" : "Compare up to 4 boats side by side and find the best value"}
                    arrow
                    placement="top-start"
                  >
                    <span>
                      <Button
                        variant={inComparator ? "contained" : "outlined"}
                        disabled={full}
                        startIcon={<CompareArrowsIcon />}
                        size="small"
                        onClick={() => {
                          if (inComparator) {
                            dispatch(removeFromComparator(yid));
                          } else {
                            const mainImg = (yacht?.yacht?.images || []).find((im: any) => im.description === "Main image");
                            const urlP = new URLSearchParams(location.search);
                            dispatch(addToComparator({
                              yachtId: yid,
                              model: yacht?.yacht?.model || 'Unknown',
                              imageUrl: mainImg?.url || '',
                              price: yacht.actualPrice || 0,
                              dateFrom: urlP.get('date_from') || yacht.dateFrom || '',
                              dateTo: urlP.get('date_to') || yacht.dateTo || '',
                            }));
                            trackEvent('compare_added', { yacht_model: yacht?.yacht?.model });
                          }
                        }}
                        sx={{
                          borderRadius: "20px",
                          alignItems: "center",
                          "& .MuiButton-startIcon": { display: "flex", alignItems: "center", marginTop: 0, marginBottom: 0 },
                          "& .MuiButton-startIcon svg": { display: "block" },
                          ...(inComparator ? {
                            bgcolor: "#1565c0",
                            "&:hover": { bgcolor: "#0d47a1" },
                          } : {
                            borderColor: "#1565c0",
                            color: "#1565c0",
                            fontWeight: 600,
                            "&:hover": { bgcolor: "#e3f0ff", borderColor: "#1565c0" },
                          }),
                        }}
                      >
                        {inComparator ? "✓ Added to comparison" : full ? "Comparator full (max 4)" : "Add to comparison"}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              );
            })()}
            <Box
              position="sticky"
              top="calc(var(--banner-height, 0px) + var(--nav-height, 68px))"
              sx={{
                display: {
                  xs: "none",
                  md: "flex",
                },
              }}
            >
              <BookingWidget
                id={yacht?.yachtId}
                price={price || 0}
                startPrice={startPrice || 0}
                discount={yacht?.discountPercentage}
                formData={formData}
                setFormData={setFormData}
                skipperData={skipperData}
                isUnavailable={!!isUnavailable}
                isOnOption={!!isOnOption}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={12}>
            <Box
              sx={{
                px: {
                  xs: "2rem",
                  md: 0,
                },
              }}
            >
              <Typography variant="h5" pb={5}>
                Other boats you might like
              </Typography>

              {similarBoats?.length > 0 ? (
                <SimilatBoatsWidget
                  similarBoats={similarBoats}
                  handleYachtClick={handleYachtClick}
                />
              ) : (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ color: "gray", p: 2, textAlign: "center" }}
                  >
                    No boats found
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default YachtDetail;
